import type { BadgeTone } from '../../ui/index.js';

/**
 * Türkçe etiket sözlükleri ve tone helpers — provider-control-plane'in
 * eski pillForXxx fonksiyonlarının Badge tone karşılıkları. Tüm mailcom
 * sayfa parçaları buradan import ediyor.
 */

export const ACCOUNT_STATUS_LABEL: Record<string, string> = {
  active: 'aktif',
  // R-D214 — 'draining' is a first-class bucket post-migration 0041.
  draining: 'draining',
  disabled: 'devre dışı',
  suspended: 'askıda',
  login_failed: 'giriş başarısız',
  pending_credentials: 'kimlik bekliyor',
};

// R-D214 — V1 8-state taxonomy secondary-badge labels. Surfaced verbatim
// from provider_accounts.status_detail by the admin list endpoint.
export const STATUS_DETAIL_LABEL: Record<string, string> = {
  active: 'Aktif',
  draining: 'Draining',
  renewal_in_progress: 'Yenileniyor',
  renewal_failed: 'Yenileme Hatası',
  alias_limit_reached: 'Alias Limiti (Retry)',
  login_failed: 'Giriş Hatası',
  disabled: 'Devre Dışı',
  bootstrapping: 'Bootstrapping',
  // 2026-09-22 — GMX parent parked at the non-skippable post-login phone
  // wall (status='disabled' + status_detail='phone_verification_required').
  phone_verification_required: 'Telefon doğrulaması',
  // 2026-09-23 (D-239) — operator "Satıştan çek" (alias-model parent,
  // status='disabled') and the runtime's GMX K-strike IMAP quarantine.
  operator_off_sale: 'Satıştan çekildi (operatör)',
  imap_rejected: 'IMAP girişi reddedildi',
};

// R-D214 — V1 cooldown-pool reason labels. Surfaced from
// provider_accounts.suspended_reason. Renders as the tertiary line in
// the row tooltip + as the badge label when status_detail is missing.
export const SUSPENDED_REASON_LABEL: Record<string, string> = {
  alias_limit_reached: 'Alias Limiti',
  account_locked: 'Hesap Kilitli',
  abuse_hard_lock: 'Abuse Hard Lock',
  invalid_grant: 'Invalid Grant',
  upstream_unavailable: 'Upstream Erişilemez',
  blocked: 'Engellendi',
  manual: 'Operatör Manuel',
  phone_verification_required: 'Telefon doğrulaması',
  operator_removed: 'Operatör kaldırdı',
};

export function toneForStatusDetail(detail: string): BadgeTone {
  // R-D214 — secondary badge tone per V1 ParentMails.jsx STATUS_CONFIG.
  if (detail === 'active') return 'success';
  if (detail === 'draining' || detail === 'bootstrapping') return 'info';
  if (detail === 'renewal_in_progress') return 'warn';
  if (detail === 'alias_limit_reached') return 'warn';
  if (detail === 'renewal_failed') return 'danger';
  if (detail === 'login_failed') return 'danger';
  if (detail === 'phone_verification_required') return 'danger';
  if (detail === 'imap_rejected') return 'danger';
  if (detail === 'operator_off_sale') return 'warn';
  if (detail === 'disabled') return 'neutral';
  return 'neutral';
}

export const SESSION_STATE_LABEL: Record<string, string> = {
  active: 'aktif',
  ok: 'aktif',
  unknown: 'bilinmiyor',
  pending: 'beklemede',
  degraded: 'sorunlu',
  error: 'hata',
};

export const ALIAS_STATUS_LABEL: Record<string, string> = {
  available: 'satışa uygun',
  reserved: 'rezerve',
  leased: 'siparişte',
  used: 'kullanıldı',
  disabled: 'devre dışı',
};

export const RENEWAL_STATUS_LABEL: Record<string, string> = {
  queued: 'kuyrukta',
  running: 'çalışıyor',
  completed: 'tamamlandı',
  failed: 'başarısız',
  deferred: 'ertelendi',
};

export const ELIGIBILITY_LABEL: Record<string, string> = {
  eligible: 'uygun',
  ineligible: 'uygun değil',
  deferred: 'ertelendi',
  pending: 'beklemede',
};

export const READINESS_WARNING_LABEL: Record<string, string> = {
  no_parent_account_registered:
    'Henüz parent mail hesabı kayıtlı değil. Önce parent ekleyin.',
  no_account_has_credential_ref:
    'Hiçbir parent hesabın şifreli kasada credential kaydı yok. Bir parent ekleyip credential bilgilerini kaydedin.',
  no_available_alias:
    'Satışa uygun alias yok. Önce alias içe aktarın veya yenileme kuyruğunu çalıştırın.',
  credential_vault_not_configured:
    'Credential kasası servis tarafında devre dışı. Master key tanımlanmadan parent credential kaydedilemez.',
  oauth_client_secret_env_missing:
    'mail.com OAuth client secret çalışma ortamında tanımlı değil; oturum yenileme yapılamaz.',
  upstream_base_url_env_missing:
    'mail.com API base URL tanımsız; provider çalışamaz.',
  oauth_client_id_env_missing:
    'mail.com OAuth client id tanımsız; oturum yenileme yapılamaz.',
  // R38 (D-153) — bridge_account_id_* warnings removed: the per-account vault
  // is the V2 truth (D-149), so the V1-bridge env is no longer surfaced.
};

export function describeWarning(code: string): string {
  return READINESS_WARNING_LABEL[code] ?? `Bilinmeyen uyarı: ${code}`;
}

export function toneForAccountStatus(status: string): BadgeTone {
  if (status === 'active') return 'success';
  // R-D214 — 'draining' renders as info (liveCount>0 but no new sellable).
  if (status === 'draining') return 'info';
  if (status === 'disabled' || status === 'login_failed' || status === 'suspended') return 'danger';
  return 'warn';
}

export function toneForSession(state: string): BadgeTone {
  if (state === 'active' || state === 'ok') return 'success';
  if (state === 'degraded' || state === 'error') return 'danger';
  return 'warn';
}

export function toneForAlias(status: string): BadgeTone {
  if (status === 'available') return 'success';
  if (status === 'leased' || status === 'reserved' || status === 'used') return 'info';
  if (status === 'disabled') return 'danger';
  return 'neutral';
}

export function toneForRenewal(status: string): BadgeTone {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'danger';
  if (status === 'running' || status === 'queued') return 'warn';
  return 'neutral';
}

export function toneForEvent(status: string): BadgeTone {
  if (status === 'published') return 'success';
  if (status === 'dead_letter') return 'danger';
  return 'warn';
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('tr-TR');
  } catch {
    return iso;
  }
}

export function fmtDateOnly(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('tr-TR');
  } catch {
    return iso;
  }
}
